import { useEffect, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Shield, X } from "lucide-react";
import { NormalButton } from "@/components/NormalButton";
import { Text, Heading } from "@/components/Typography";
import { AgreeAndSignOffIcon } from "@/components/icons/icons";
import { cn } from "@/utils/cn";
import { CheckBox } from "@/components/CheckBox";

interface CodeOfConductModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isLoading?: boolean;
  /** "advisor" (default) or "achiever" — controls title, intro text, and section content */
  variant?: "advisor" | "achiever";
}

const ADVISOR_CONDUCT_SECTIONS = [
  {
    heading: "1. Provide Just-in-Time Expertise",
    body: "Your role is to provide actionable feedback based on your lived experience. Focus on being a mentor, not a consultant. If a topic falls outside your expertise, be direct about it.",
  },
  {
    heading: "2. Absolute Confidentiality",
    body: "Everything shared by an Achiever is strictly confidential. This includes their professional challenges, company data, and personal goals. This commitment remains in place even after the advisory relationship ends.",
  },
  {
    heading: "3. Reliability is Key",
    body: "Achievers rely on your consistency to maintain momentum. Respond to messages within 48 hours and provide at least 24 hours' notice if you must reschedule a session.",
  },
  {
    heading: "4. Honest, Direct Feedback",
    body: "The most valuable gift you can give an Achiever is the truth. Provide candid, constructive criticism even when it’s difficult.",
  },
  {
    heading: "5. No Solicitation or Conflicts",
    body: "To maintain the integrity of the board, do not solicit Achievers for outside business, investments, or independent coaching for 12 months following the engagement. Disclose any potential conflicts of interest (such as a financial interest in their employer) to AchieveOS immediately.",
  },
  {
    heading: "6. Respect & Integrity",
    body: "We have zero tolerance for harassment or discrimination. Maintain a professional, supportive environment in every interaction.",
  },
  {
    heading: "7. Intellectual Property",
    body: "You retain ownership of your original frameworks. Likewise, any strategies developed by the Achiever remain their property. Neither party may commercialize shared insights without explicit permission.",
  },
];

const ACHIEVER_CONDUCT_SECTIONS = [
  {
    heading: "1. Respect their time",
    body: "Your experts are busy professionals. Honor their investment by arriving on time, staying for the full session, and completing any between-meeting actions you agreed upon.",
  },
  {
    heading: "2. Own your outcomes",
    body: "The system provides the structure and the experts provide the expertise, but you provide the momentum. You are responsible for setting your goals and taking the lead on your career decisions.",
  },
  {
    heading: "3. Keep it confidential",
    body: "Trust is the foundation of a Personal Advisory Board. Do not share personal anecdotes, strategic frameworks, or internal company information shared during sessions with anyone outside the board.",
  },
  {
    heading: "4. Be radically honest",
    body: "Advisory boards fail when information is withheld. Share your real challenges and setbacks. If you disagree with advice or feel a relationship isn't working, speak up early rather than disengaging.",
  },
  {
    heading: "5. Prepare to be coached",
    body: "Your experts are here to provide candid feedback. Receive it with an open mind. Come to every session with a clear agenda and specific questions so your board can be helpful immediately.",
  },
  {
    heading: "6. Keep it professional",
    body: "AchieveOS is a space for growth, not solicitation. Do not use these relationships to pitch business, seek investment, or ask for jobs. Maintain professional boundaries in all platform messages and sessions.",
  },
  {
    heading: "7. Protect the community",
    body: "Any form of harassment, discrimination, or misuse of the platform will result in immediate removal.",
  },
];

export const CodeOfConductModal = ({
  open,
  onOpenChange,
  onConfirm,
  isLoading = false,
  variant = "advisor",
}: CodeOfConductModalProps) => {
  const isAchiever = variant === "achiever";
  const sections = isAchiever ? ACHIEVER_CONDUCT_SECTIONS : ADVISOR_CONDUCT_SECTIONS;
  const modalTitle = isAchiever ? "Code of Conduct" : "Expert Code of Conduct";
  const introText = isAchiever
    ? "This program works because of the commitment between you and your experts. Please review and sign these standards before your first session."
    : "Thank you for sharing your expertise. To ensure a high-impact experience for your Achiever, we ask all Experts to commit to the following principles.";
  const checkboxText = isAchiever
    ? "I Commit to these standards"
    : "I accept these responsibilities";
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [sectionChecks, setSectionChecks] = useState<boolean[]>(() =>
    Array(sections.length).fill(false),
  );
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = Math.abs(el.scrollHeight - el.scrollTop - el.clientHeight) < 24;
    if (atBottom) setHasScrolledToBottom(true);
  };

  const handleOpenChange = (value: boolean) => {
    if (!value) {
      setHasScrolledToBottom(false);
      setAgreed(false);
      setSectionChecks(Array(sections.length).fill(false));
    }
    onOpenChange(value);
  };

  const handleSectionCheck = (index: number, checked: boolean) => {
    setSectionChecks((prev) => {
      const next = [...prev];
      next[index] = checked;
      return next;
    });
  };

  // Keep the final sign-off checkbox in sync with the section checkboxes in
  // both directions — checked only while every section is checked, and
  // immediately unchecked again the moment any one of them is unchecked.
  useEffect(() => {
    setAgreed(sectionChecks.length > 0 && sectionChecks.every(Boolean));
  }, [sectionChecks]);

  const canSignOff = hasScrolledToBottom && agreed;

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50" />

        <Dialog.Content
          className={cn(
            "fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
            "w-full max-w-[670px] bg-surface-card rounded-xl shadow-xl focus:outline-none",
            "flex flex-col max-h-[85vh] overflow-hidden",
          )}
        >
          {/* ── Header ── */}
          <div className="flex items-center justify-between px-7 h-[87px] border-b border-surface-border flex-shrink-0">
            <div className="flex items-center gap-3">
              {/* Shield icon box */}
              <div className="flex items-center justify-center w-10 h-10 rounded-[14px] border border-surface-border bg-surface-card-inner shadow-[0px_1px_3px_0px_rgba(0,0,0,0.10),0px_1px_2px_-1px_rgba(0,0,0,0.10)] flex-shrink-0">
                <Shield size={18} className="text-primary-750" />
              </div>
              <div>
                <Heading
                  as="h2"
                  className="text-theme-text-heading font-bold text-[18px] leading-7 tracking-[-0.44px]"
                >
                  {modalTitle}
                </Heading>
                <Text as="p" className="text-theme-text-muted text-[12px] leading-4 mt-0.5">
                  Please read carefully and sign off at the bottom
                </Text>
              </div>
            </div>

            {/* Close button */}
            <Dialog.Close asChild>
              <button
                className="flex items-center justify-center w-8 h-8 rounded-lg text-theme-text-muted hover:text-theme-text-heading hover:bg-surface-card-inner transition-colors flex-shrink-0"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </Dialog.Close>
          </div>

          {/* ── Scroll banner ── */}
          {!hasScrolledToBottom && (
            <div className="flex items-center justify-center py-[11px] border-b border-surface-border bg-[var(--theme-coc-scroll-banner-bg)] flex-shrink-0">
              <Text
                as="span"
                className="text-[var(--theme-coc-scroll-banner-text)] text-[12px] leading-4 text-center"
              >
                Scroll to the bottom to enable sign-off
              </Text>
            </div>
          )}

          {/* ── Scrollable content ── */}
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="overflow-y-auto flex-1 px-7 py-6 space-y-6"
          >
            {/* Intro paragraph */}
            <Text
              as="p"
              className="text-theme-text-muted text-[16px] leading-[26px] tracking-[-0.31px]"
            >
              {introText}
            </Text>

            {sections.map((section, index) => (
              <div key={section.heading} className="space-y-2">
                <CheckBox
                  id={`conduct-section-${index}`}
                  checked={sectionChecks[index] ?? false}
                  onChange={(checked) => handleSectionCheck(index, checked)}
                >
                  <Text
                    as="span"
                    className="text-theme-text-heading font-semibold text-[14px] leading-5 tracking-[-0.15px]"
                  >
                    {section.heading}
                  </Text>
                </CheckBox>
                <Text
                  as="p"
                  className="text-theme-text-muted text-[14px] leading-[22.75px] tracking-[-0.15px] pl-8"
                >
                  {section.body}
                </Text>
              </div>
            ))}

            {/* Acknowledgment checkbox box — entire container is clickable */}
            <div
              className="rounded-[14px] border-2 p-[15px] cursor-pointer"
              style={{
                borderColor: "var(--color-okt-selected-border)",
                backgroundColor: "var(--color-okt-selected-bg)",
              }}
              onClick={(e) => {
                // Skip if click came from the Radix checkbox button or its label — they handle toggling themselves
                if (!(e.target as HTMLElement).closest('[role="checkbox"], label')) {
                  setAgreed((v) => !v);
                }
              }}
            >
              <CheckBox id="conduct-agree" checked={agreed} onChange={setAgreed}>
                <Text
                  as="span"
                  className="text-secondary-900 dark:text-white font-medium text-[14px] leading-[22.75px] tracking-[-0.15px]"
                >
                  {checkboxText}
                </Text>
              </CheckBox>
            </div>
          </div>

          {/* ── Footer ── */}
          <div className="flex items-start gap-3 px-7 pt-5 pb-5 border-t border-surface-border flex-shrink-0">
            {/* Cancel */}
            <NormalButton
              size="md"
              borderColor="border-surface-border"
              bgColor="bg-surface-card"
              textColor="text-theme-text-heading"
              hoverBgColor="hover:bg-surface-card-inner"
              className="flex-1 h-[46px] rounded-[14px] justify-center text-[14px] font-semibold leading-5 tracking-[-0.15px]"
              onClick={() => handleOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </NormalButton>

            {/* I Agree & Sign Off */}
            <NormalButton
              borderColor="border-transparent"
              bgColor=""
              textColor="text-white"
              hoverBgColor=""
              disabled={!canSignOff || isLoading}
              isLoading={isLoading}
              leftIcon={<AgreeAndSignOffIcon size={16} className="flex-shrink-0 text-white" />}
              onClick={onConfirm}
              className={cn(
                "goals-new-btn flex-1 h-[46px] rounded-[10px]",
                "inline-flex items-center justify-center",
                "text-[14px] font-semibold leading-5 tracking-[-0.15px]",
                "transition-opacity",
                (!canSignOff || isLoading) && "opacity-50 cursor-not-allowed",
              )}
            >
              {isLoading ? "Signing off..." : "I Agree & Sign Off"}
            </NormalButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default CodeOfConductModal;
